// SCR-STAFF-301 — برگه روز پذیرش (FLOW-03؛ US-SCH-061، 033، 021، 032): «امروز · شعبه · تاریخ»، فیلتر ارائه‌دهنده و وضعیت، جستجو،
// «+ بدون نوبت» (Alt+W)، ستون‌های ساعت، مراجعه‌کننده، خدمت (حساس ← «ویزیت»)، ارائه‌دهنده، اتاق، وضعیت، ✓ تأیید، ₪ بیعانه یا پکیج، 📄
// فرم ناقص و اقدام. ↑↓ بین ردیف‌ها، Enter = CheckIn، R = جابه‌جایی، X = لغو با دلیل، N = غیبت. به‌روزرسانی زنده با برجسته شدن ردیف.
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { unwrap, type AppointmentStatus, type DaySheetRow } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, ErrorState, faDigits, LoadingState, PageHeader, ReasonDialog, latinDigits } from '@phoenix/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { useLockPolicy } from './appointmentLock';
import { CancelAppointmentDialog, useAppointmentMutations } from './AppointmentActions';
import {
  addDays,
  appointmentPath,
  AppointmentStatusChip,
  BranchRequired,
  DepositPaymentChip,
  formatDay,
  formatTime,
  fromUrlDate,
  schKeys,
  toUrlDate,
  useActiveBranch,
} from './common';
import { useAppointmentsChanged } from './realtime';
import { useSchedulingActions } from './SchedulingActions';
import { CredentialAlertsBanner } from '../iam/CredentialAlertsBanner';

export const SCR_STAFF_301 = 'SCR-STAFF-301';

const STATUSES: readonly AppointmentStatus[] = ['Booked', 'Confirmed', 'CheckedIn', 'NoShow', 'Cancelled', 'NeedsReschedule'];
const CHECK_IN_FROM: readonly AppointmentStatus[] = ['Booked', 'Confirmed'];
const ACTIVE: readonly AppointmentStatus[] = ['Booked', 'Confirmed', 'NeedsReschedule'];

function matches(row: DaySheetRow, text: string): boolean {
  if (!text) {
    return true;
  }
  const haystack = latinDigits(`${row.patientName ?? ''} ${row.fileNumber ?? ''} ${row.serviceName ?? ''} ${row.providerName ?? ''}`);
  return haystack.toLowerCase().includes(latinDigits(text).toLowerCase());
}

export function DaySheetPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const scheduling = useSchedulingActions();
  const { branchId, branchName } = useActiveBranch();
  const [params, setParams] = useSearchParams();
  const date = fromUrlDate(params.get('date'));
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<DaySheetRow | null>(null);
  const [lockedNoShow, setLockedNoShow] = useState<DaySheetRow | null>(null);
  const [formsWarning, setFormsWarning] = useState<DaySheetRow | null>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const { checkIn, noShow } = useAppointmentMutations();
  const lock = useLockPolicy();
  const full = access.accessMode === 'Full';
  const can = {
    checkIn: full && access.can('sch.appointment.checkin'),
    noShow: full && access.can('sch.appointment.mark_no_show'),
    cancel: full && access.can('sch.appointment.cancel'),
    reschedule: scheduling?.canReschedule ?? false,
    walkIn: scheduling?.canWalkIn ?? false,
  };

  const sheet = useQuery({
    queryKey: schKeys.daySheet(session?.tenantId, branchId, date),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/day-sheet', {
          params: { query: { branchId: branchId ?? undefined, date: date ?? undefined } },
        }),
      ),
    enabled: branchId !== null,
    refetchInterval: 60_000,
  });
  const view = sheet.data;
  const timeZone = view?.timeZone;

  // به‌روزرسانی زنده: بازخوانی و برجسته شدن ردیف‌های تغییرکرده.
  useAppointmentsChanged((message) => {
    if (message.branchId !== branchId) {
      return;
    }
    setHighlighted(new Set(message.appointmentIds));
    void queryClient.invalidateQueries({ queryKey: ['scheduling', session?.tenantId, 'day-sheet'] });
  });
  useEffect(() => {
    if (highlighted.size === 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => setHighlighted(new Set()), 4000);
    return () => window.clearTimeout(timer);
  }, [highlighted]);

  const rows = useMemo(
    () =>
      (view?.rows ?? []).filter(
        (row) =>
          (!provider || row.providerId === provider) &&
          (!status || (status === 'possible_no_show' ? row.possibleNoShow : row.status === status)) &&
          matches(row, search),
      ),
    [view, provider, status, search],
  );

  useEffect(() => {
    if (rows.length > 0 && !rows.some((row) => row.appointmentId === selectedId)) {
      setSelectedId(rows[0]?.appointmentId ?? null);
    }
  }, [rows, selectedId]);

  const name = (row: DaySheetRow) => row.patientName ?? '';
  const isToday = view?.isToday ?? false;

  const doCheckIn = (row: DaySheetRow, confirmed = false) => {
    if (!can.checkIn || !isToday || !CHECK_IN_FROM.includes(row.status)) {
      return;
    }
    if (row.formsIncomplete && !confirmed) {
      setFormsWarning(row);
      return;
    }
    checkIn.mutate(row.appointmentId, {
      onSuccess: () => {
        setToast(
          row.depositState === 'unpaid'
            ? `${t('sch:day.checked_in', { name: name(row) })} ${t('sch:day.unpaid_deposit_warning')}`
            : t('sch:day.checked_in', { name: name(row) }),
        );
      },
      onError: () => undefined,
    });
  };

  const doNoShow = (row: DaySheetRow) => {
    if (!can.noShow || !ACTIVE.includes(row.status) || (lock.isLocked(row.status) && !lock.canOverride)) {
      return;
    }
    if (lock.isLocked(row.status)) {
      setLockedNoShow(row);
      return;
    }
    noShow.mutate({ id: row.appointmentId }, { onSuccess: () => setToast(t('sch:day.no_show_done', { name: name(row) })) });
  };

  const doReschedule = (row: DaySheetRow) => {
    if (!can.reschedule || !ACTIVE.includes(row.status) || (lock.isLocked(row.status) && !lock.canOverride)) {
      return;
    }
    scheduling?.openReschedule({
      appointmentId: row.appointmentId,
      rowVersion: row.rowVersion,
      patient: { id: row.patientId, displayName: name(row), fileNumber: row.fileNumber },
      date: view?.date ?? null,
      providerId: row.providerId,
      status: row.status,
    });
  };

  const doCancel = (row: DaySheetRow) => {
    if (can.cancel && ACTIVE.includes(row.status) && !(lock.isLocked(row.status) && !lock.canOverride)) {
      setCancelling(row);
    }
  };

  const focusRow = (id: string | undefined) => {
    if (!id) {
      return;
    }
    setSelectedId(id);
    rowRefs.current.get(id)?.focus();
  };

  const onRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: DaySheetRow, index: number) => {
    if (event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const key = event.key.toLowerCase();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow(rows[Math.min(index + 1, rows.length - 1)]?.appointmentId);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusRow(rows[Math.max(index - 1, 0)]?.appointmentId);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      doCheckIn(row);
    } else if (key === 'r' || event.code === 'KeyR') {
      event.preventDefault();
      doReschedule(row);
    } else if (key === 'x' || event.code === 'KeyX') {
      event.preventDefault();
      doCancel(row);
    } else if (key === 'n' || event.code === 'KeyN') {
      event.preventDefault();
      doNoShow(row);
    }
  };

  const setDate = (next: string | null) => {
    const copy = new URLSearchParams(params);
    if (next) {
      copy.set('date', toUrlDate(next));
    } else {
      copy.delete('date');
    }
    setParams(copy, { replace: true });
  };

  const mutationError = checkIn.error ?? noShow.error;

  return (
    <Stack data-screen-id={SCR_STAFF_301} spacing={2}>
      <CredentialAlertsBanner />
      <PageHeader
        titleKey="sch:day.title"
        actions={
          can.walkIn ? (
            <Button
              variant="contained"
              onClick={() => scheduling?.openWalkIn()}
              aria-keyshortcuts="Alt+W"
              data-testid="day-walk-in"
            >
              {t('sch:day.walk_in')}
              <Typography component="kbd" variant="caption" dir="ltr" sx={{ ml: 1, opacity: 0.8 }}>
                {t('sch:quick.walk_in_shortcut')}
              </Typography>
            </Button>
          ) : undefined
        }
      />
      {view && (
        <Typography variant="subtitle1" component="p" data-testid="day-heading">
          {t('sch:day.heading', { branch: branchName ?? '', date: formatDay(view.date) })}
        </Typography>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={t('sch:day.prev_day')}>
            <span>
              <IconButton aria-label={t('sch:day.prev_day')} disabled={!view} onClick={() => view && setDate(addDays(view.date, -1))}>
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => setDate(null)} disabled={isToday}>
            {t('sch:day.go_today')}
          </Button>
          <Tooltip title={t('sch:day.next_day')}>
            <span>
              <IconButton aria-label={t('sch:day.next_day')} disabled={!view} onClick={() => view && setDate(addDays(view.date, 1))}>
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <TextField
          select
          size="small"
          label={t('sch:day.filter_provider')}
          value={provider}
          onChange={(event) => setProvider(event.target.value)}
          sx={{ minWidth: 200 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">{t('sch:day.all_providers')}</MenuItem>
          {(view?.providers ?? []).map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('sch:day.filter_status')}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 180 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'day-status-filter' }}
        >
          <MenuItem value="">{t('sch:status.all')}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`sch:status.${value}`)}
            </MenuItem>
          ))}
          <MenuItem value="possible_no_show">{t('sch:status.possible_no_show')}</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="search"
          label={t('sch:day.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('sch:day.shortcuts')}
      </Typography>
      {mutationError ? <ErrorState error={mutationError} compact /> : null}
      {branchId === null ? (
        <BranchRequired />
      ) : sheet.isPending ? (
        <LoadingState rows={5} />
      ) : sheet.error ? (
        <ErrorState error={sheet.error} onRetry={() => void sheet.refetch()} />
      ) : rows.length === 0 ? (
        <Alert severity="info" data-testid="day-empty">
          {(view?.rows.length ?? 0) === 0 ? t('sch:day.empty') : t('sch:day.empty_filtered')}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('sch:day.table_label')} data-testid="day-sheet">
            <TableHead>
              <TableRow>
                <TableCell>{t('sch:day.col_time')}</TableCell>
                <TableCell>{t('sch:day.col_patient')}</TableCell>
                <TableCell>{t('sch:day.col_service')}</TableCell>
                <TableCell>{t('sch:day.col_provider')}</TableCell>
                <TableCell>{t('sch:day.col_room')}</TableCell>
                <TableCell>{t('sch:day.col_status')}</TableCell>
                <TableCell align="center" title={t('sch:day.col_confirmed')}>
                  <span aria-label={t('sch:day.col_confirmed')}>✓</span>
                </TableCell>
                <TableCell align="center" title={t('sch:day.col_payment')}>
                  <span aria-label={t('sch:day.col_payment')}>₪</span>
                </TableCell>
                <TableCell align="center" title={t('sch:day.col_forms')}>
                  <DescriptionOutlinedIcon fontSize="small" aria-label={t('sch:day.col_forms')} />
                </TableCell>
                <TableCell>{t('sch:day.col_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const selected = row.appointmentId === selectedId;
                const flash = highlighted.has(row.appointmentId);
                return (
                  <TableRow
                    key={row.appointmentId}
                    ref={(element) => {
                      if (element) {
                        rowRefs.current.set(row.appointmentId, element);
                      } else {
                        rowRefs.current.delete(row.appointmentId);
                      }
                    }}
                    hover
                    selected={selected}
                    tabIndex={selected ? 0 : -1}
                    aria-selected={selected}
                    onFocus={() => setSelectedId(row.appointmentId)}
                    onClick={() => setSelectedId(row.appointmentId)}
                    onKeyDown={(event) => onRowKeyDown(event, row, index)}
                    data-testid="day-row"
                    data-appointment-id={row.appointmentId}
                    data-status={row.status}
                    data-highlighted={flash ? 'true' : undefined}
                    sx={(theme) => ({
                      outline: 'none',
                      transition: 'background-color 0.6s',
                      ...(flash ? { bgcolor: alpha(theme.palette.warning.main, 0.18) } : {}),
                      '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` },
                    })}
                  >
                    <TableCell>
                      <bdi>{formatTime(row.start, timeZone)}</bdi>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" component="span">
                          {row.patientName}
                        </Typography>
                        {row.fileNumber && (
                          <Typography variant="caption" color="text.secondary" component="span">
                            {faDigits(row.fileNumber)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.serviceMasked ? t('sch:day.masked_service') : row.serviceName}</TableCell>
                    <TableCell>{row.providerName}</TableCell>
                    <TableCell>{row.roomName ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {row.status === 'CheckedIn' && row.checkedInAt ? (
                          <Chip
                            size="small"
                            color="secondary"
                            label={t('sch:day.checked_in_at', { time: formatTime(row.checkedInAt, timeZone) })}
                          />
                        ) : (
                          <AppointmentStatusChip status={row.status} possibleNoShow={row.possibleNoShow} />
                        )}
                        {row.type === 'WalkIn' && <Chip size="small" variant="outlined" label={t('sch:day.walk_in_badge')} />}
                        {row.isOverbook && <Chip size="small" variant="outlined" color="warning" label={t('sch:day.overbook')} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {row.confirmed === true ? (
                        <CheckIcon fontSize="small" color="success" aria-label={t('sch:day.confirmed_yes')} />
                      ) : row.confirmed === false ? (
                        <CloseIcon fontSize="small" color="error" aria-label={t('sch:day.confirmed_no')} />
                      ) : null}
                    </TableCell>
                    <TableCell align="center">
                      {row.packageLinked ? (
                        <span title={t('sch:day.package_label')}>{t('sch:day.package')}</span>
                      ) : (
                        <DepositPaymentChip state={row.depositState} dueAt={row.depositDueAt} timeZone={timeZone} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {row.formsIncomplete ? (
                        <span role="img" aria-label={t('sch:day.forms_incomplete')} title={t('sch:day.forms_incomplete')}>
                          ⚠
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {can.checkIn && isToday && CHECK_IN_FROM.includes(row.status) && !row.possibleNoShow && (
                          <Button
                            size="small"
                            variant={selected ? 'contained' : 'outlined'}
                            tabIndex={-1}
                            onClick={() => doCheckIn(row)}
                            disabled={checkIn.isPending}
                            data-testid="day-check-in"
                          >
                            {t('sch:day.check_in')}
                            {selected ? ' ⏎' : ''}
                          </Button>
                        )}
                        {row.possibleNoShow && can.checkIn && isToday && CHECK_IN_FROM.includes(row.status) && (
                          <Button size="small" tabIndex={-1} onClick={() => doCheckIn(row)} data-testid="day-check-in">
                            {t('sch:day.check_in')}
                          </Button>
                        )}
                        {row.possibleNoShow && can.noShow && (
                          <Button size="small" color="error" tabIndex={-1} onClick={() => doNoShow(row)} data-testid="day-no-show">
                            {t('sch:day.no_show')}
                          </Button>
                        )}
                        {row.status === 'CheckedIn' && access.can('bil.visit.view') && (
                          // AP-BIL-01 (FLOW-04): مراجعه خودکار پس از CheckIn؛ ورود به SCR-STAFF-400 از ردیف.
                          <Button
                            size="small"
                            tabIndex={-1}
                            component={RouterLink}
                            to={`/visits/by-appointment/${row.appointmentId}`}
                            data-testid="day-visit"
                          >
                            {t('sch:day.visit')}
                          </Button>
                        )}
                        <Button
                          size="small"
                          tabIndex={-1}
                          component={RouterLink}
                          to={appointmentPath(row.appointmentId)}
                          data-testid="day-details"
                        >
                          {t('sch:day.details')}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.some((row) => row.appointmentId === selectedId && row.depositState === 'unpaid') ? (
        <Alert severity="warning" data-testid="day-unpaid-deposit">
          {t('sch:day.unpaid_deposit_warning')}
        </Alert>
      ) : null}
      <Box>
        <Chip size="small" variant="outlined" label={t('sch:day.live')} />
      </Box>

      <CancelAppointmentDialog
        open={cancelling !== null}
        appointmentId={cancelling?.appointmentId ?? null}
        patientName={cancelling?.patientName}
        locked={cancelling ? lock.isLocked(cancelling.status) : false}
        onClose={() => setCancelling(null)}
        onCancelled={(cancelled) => {
          setCancelling(null);
          setToast(t('sch:detail.cancelled'));
          setHighlighted(new Set([cancelled.id]));
        }}
      />
      <ReasonDialog
        open={lockedNoShow !== null}
        titleKey="sch:detail.no_show_title"
        messageKey="sch:detail.locked_no_show"
        confirmKey="sch:detail.no_show"
        minLength={10}
        destructive
        busy={noShow.isPending}
        error={noShow.error}
        onConfirm={(text) => {
          if (!lockedNoShow) {
            return;
          }
          noShow.mutate(
            { id: lockedNoShow.appointmentId, overrideLock: true, overrideReason: text },
            {
              onSuccess: () => {
                setToast(t('sch:day.no_show_done', { name: name(lockedNoShow) }));
                setLockedNoShow(null);
              },
            },
          );
        }}
        onCancel={() => setLockedNoShow(null)}
      />
      <ConfirmDialog
        open={formsWarning !== null}
        titleKey="sch:day.forms_warning_title"
        messageKey="sch:day.forms_warning_message"
        confirmKey="sch:day.forms_continue"
        onConfirm={() => {
          const row = formsWarning;
          setFormsWarning(null);
          if (row) {
            doCheckIn(row, true);
          }
        }}
        onCancel={() => setFormsWarning(null)}
      />
      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)} data-testid="day-toast">
          {toast}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
